import React, { useState, useEffect } from 'react';

import { Drawer } from '@modules';
import { Button } from '@components';

import { t } from 'ttag';

import { useSiteSettings } from '../context/SiteSettingsContext';

export default function SiteDetails() {
  const DISPLAY_SITES_TITLES = [
    { domain: 'Unisport.dk', country: 'Danmark' },
    { domain: 'Unisportstore.se', country: 'Sverige' },
    { domain: 'Unisportstore.no', country: 'Norge' },
    { domain: 'Unisportstore.fi', country: 'Suomi' },
    { domain: 'Unisportstore.nl', country: 'Nederland' },
    { domain: 'Unisportstore.de', country: 'Deutschland' },
    { domain: 'Unisportstore.fr', country: 'France' },
    { domain: 'Unisportstore.at', country: 'Österreich' },
    { domain: 'Unisportstore.com', country: 'International' },
    { domain: 'Unisport.pl', country: 'Polska' },
    { domain: 'Unisportstore.it', country: 'Italia' }
  ];

  const { currentSiteSettings, allSiteSettings } = useSiteSettings();

  const [paymentIcons, setPaymentIcons] = useState([]);
  const [countrySites, setCountrySites] = useState([]);
  const [currentCountry, setCurrentCountry] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    const orderedSites = DISPLAY_SITES_TITLES.map(displaySite => {
      const siteData = allSiteSettings.find(
        site => site.site.name === displaySite.domain
      );

      return siteData
        ? {
            title: displaySite.country,
            slug: siteData.site.domain,
            flag: siteData.flag
          }
        : null;
    }).filter(Boolean);

    setCountrySites(orderedSites);

    if (currentSiteSettings && currentSiteSettings.length > 0) {
      const currentSiteData = currentSiteSettings[0];

      const correspondingDisplaySite = DISPLAY_SITES_TITLES.find(
        site => site.domain === currentSiteData.site.name
      );

      const currentCountryTitle = correspondingDisplaySite
        ? correspondingDisplaySite.country
        : currentSiteData.site.name;

      setCurrentCountry({
        slug: currentSiteData.site.domain,
        title: currentCountryTitle
      });

      setPaymentIcons(currentSiteData.payment_icons_links);
    }
  }, [currentSiteSettings, allSiteSettings]);

  return (
    <section className="min-h-[192px] space-y-4 px-3 pb-12 pt-4 lg:pb-20 lg:pt-8">
      <div className="md:flex md:justify-between">
        <div>
          {currentCountry && (
            <div className="flex items-center justify-start pb-8">
              <svg
                className="mr-2 h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
              >
                <path d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM9.71002 19.6674C8.74743 17.6259 8.15732 15.3742 8.02731 13H4.06189C4.458 16.1765 6.71639 18.7747 9.71002 19.6674ZM10.0307 13C10.1811 15.4388 10.8778 17.7297 12 19.752C13.1222 17.7297 13.8189 15.4388 13.9693 13H10.0307ZM19.9381 13H15.9727C15.8427 15.3742 15.2526 17.6259 14.29 19.6674C17.2836 18.7747 19.542 16.1765 19.9381 13ZM4.06189 11H8.02731C8.15732 8.62577 8.74743 6.37407 9.71002 4.33256C6.71639 5.22533 4.458 7.8235 4.06189 11ZM10.0307 11H13.9693C13.8189 8.56122 13.1222 6.27025 12 4.24799C10.8778 6.27025 10.1811 8.56122 10.0307 11ZM14.29 4.33256C15.2526 6.37407 15.8427 8.62577 15.9727 11H19.9381C19.542 7.8235 17.2836 5.22533 14.29 4.33256Z" />
              </svg>

              <Button
                key={currentCountry.name}
                aria-label="Navigate"
                variant="tertiary"
                className="p-0 focus:outline-none"
                onClick={() => setIsDrawerOpen(true)}
              >
                <Button.Text className="pl-0 font-bold">
                  {currentCountry.title}
                </Button.Text>
                <Button.Icon className="box-border flex h-6 w-6 shrink-0 items-center justify-center ">
                  <svg
                    className="h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                  >
                    <path d="M11.9997 13.1714L16.9495 8.22168L18.3637 9.63589L11.9997 15.9999L5.63574 9.63589L7.04996 8.22168L11.9997 13.1714Z" />
                  </svg>
                </Button.Icon>
              </Button>
            </div>
          )}

          <Drawer isOpen={isDrawerOpen} setIsOpen={setIsDrawerOpen}>
            <Drawer.Close />
            <Drawer.Title className="text-xl font-bold">
              {t`Change Country`}
            </Drawer.Title>
            <Drawer.Content>
              {countrySites.map(({ title, slug, flag }) => (
                <Button
                  key={slug}
                  aria-label="Navigate"
                  variant="tertiary"
                  href={slug}
                  className="mt-1 flex h-[56px] w-full justify-start space-x-3 rounded-md border border-none bg-gray bg-opacity-30 text-base font-bold text-black hover:bg-opacity-100"
                  onClick={() => {
                    setCurrentCountry({ title, slug });
                    setIsDrawerOpen(false);
                    window.location.href = `https://${slug}`;
                  }}
                >
                  <Button.Icon>
                    <div className="relative h-[100%] w-[100%] overflow-hidden rounded-full">
                      <img
                        src={flag}
                        alt={`${title} flag`}
                        className="layout-fill absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform object-cover"
                        style={{
                          width: '120%',
                          height: '160%'
                        }}
                      />
                    </div>
                  </Button.Icon>

                  <Button.Text className="pl-0 font-bold">{title}</Button.Text>
                </Button>
              ))}
            </Drawer.Content>
          </Drawer>
          <div className="m-0 flex flex-wrap items-center justify-start gap-2">
            {Object.entries(paymentIcons)?.map(([key, value]) => (
              <img
                key={key}
                src={value}
                alt={`${key} icon`}
                className="mr-2 block h-[28px] w-[42px] border border-solid border-whisper"
              />
            ))}
          </div>
        </div>
        {currentCountry && currentCountry.slug === 'www.unisport.dk' && (
          <div className="flex-start mt-4 flex space-x-4">
            <img
              className="w-20"
              src="https://static-paths.uniid.it/Footer/Pricerunner.png"
              alt="Pricerunner Award"
            />
            {/* Placeholder for DBU logo */}
          </div>
        )}
      </div>
    </section>
  );
}
